/**
 * Global "taste match" indicator.
 *
 * Wraps the pure scoring in `lib/tasteScore.ts` in an app-wide provider so a
 * match badge can be shown on *every* surface that renders events — the
 * `/events` list, the map popups, the event modal, venue/series detail pages,
 * and the My taste page — without each page having to rebuild a taste profile.
 *
 * Why a provider (and not per-page profiles):
 *   The taste profile is built from the events the user has marked as "taste".
 *   Pages like VenueDetail / SeriesDetail only load a *scoped* slice of events
 *   that usually doesn't contain the marked set, so they can't build a profile
 *   locally. Instead, pages that DO load the full upcoming-events window
 *   (HomePage, MyTastePage, EventsMapPage) call {@link registerEvents}, which
 *   persists a lightweight snapshot of each marked event to localStorage. Every
 *   other page then scores against that persisted profile for free.
 *
 * Cost: entirely client-side. No API calls are made here — embedding blending
 * is optional and only supplied by pages that already fetch embedding scores.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useFavorites } from './favorites';
import { useTasteSettings } from './tasteSettings';
import {
  buildTasteProfile,
  scoreEvent,
  MIN_TASTE_FOR_SCORING,
  MIN_TASTE_SCORE_THRESHOLD,
  type TasteProfileInput,
  type TasteReason,
} from './tasteScore';
import type { ExtractedEvent } from '../types';

const STORAGE_KEY = 'mh.tasteSnapshots.v1';

/** Score at/above which a match is labelled "high". */
const HIGH_THRESHOLD = 0.5;
/** Score at/above which a match is labelled "medium". */
const MEDIUM_THRESHOLD = 0.3;

export type MatchLevel = 'high' | 'medium' | 'low';

export interface TasteMatch {
  /** Raw similarity score in [0, 1]. */
  score: number;
  /** `score` rendered as an integer percentage 0–100. */
  percent: number;
  /** Bucketed strength for color coding + labels. */
  level: MatchLevel;
  /** Top contributing signals (already truncated by `scoreEvent`). */
  reasons: TasteReason[];
}

/** Bucket a raw score into a {@link MatchLevel}. */
export function matchLevel(score: number): MatchLevel {
  if (score >= HIGH_THRESHOLD) return 'high';
  if (score >= MEDIUM_THRESHOLD) return 'medium';
  return 'low';
}

/** Short human label for a level (used in tooltips / the modal badge). */
export function matchLevelLabel(level: MatchLevel): string {
  switch (level) {
    case 'high': return 'Great match';
    case 'medium': return 'Good match';
    case 'low': return 'Match';
  }
}

interface TasteMatchContextValue {
  /** True once the taste profile has enough marked events to score. */
  ready: boolean;
  /** Number of marked events currently backing the profile. */
  profileSize: number;
  /** Score a single event. Returns null when the profile is too small or the
   *  event is below the meaningful-match threshold. */
  match: (event: ExtractedEvent, embeddingScore?: number) => TasteMatch | null;
  /** Feed a batch of loaded events; marked ones are snapshotted + persisted so
   *  the profile is available on pages that don't load the marked set. */
  registerEvents: (events: ReadonlyArray<ExtractedEvent>) => void;
}

const TasteMatchContext = createContext<TasteMatchContextValue | null>(null);

/** Extract only the fields needed to build a profile. */
function toSnapshot(e: ExtractedEvent): TasteProfileInput {
  return {
    rowKey: e.rowKey,
    tags: e.tags ?? [],
    category: e.category,
    eventType: e.eventType,
    venueId: e.venueId,
    seriesId: e.seriesId,
    neighborhood: e.neighborhood,
  };
}

function readCachedSnapshots(): TasteProfileInput[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is TasteProfileInput => !!s && typeof s.rowKey === 'string');
  } catch {
    return [];
  }
}

function writeCachedSnapshots(snapshots: TasteProfileInput[]): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshots)); }
  catch { /* quota — non-fatal */ }
}

export function TasteMatchProvider({ children }: { children: ReactNode }) {
  const favorites = useFavorites();
  const tasteSettings = useTasteSettings();

  // Persisted snapshots of marked events, keyed by rowKey. Hydrated
  // synchronously so a profile is available on the very first paint.
  const [snapshots, setSnapshots] = useState<Map<string, TasteProfileInput>>(
    () => new Map(readCachedSnapshots().map((s) => [s.rowKey, s]))
  );
  // Guards registerEvents against redundant state updates (avoids render loops).
  const snapshotKeysRef = useRef<Set<string>>(new Set(snapshots.keys()));

  const registerEvents = useCallback((events: ReadonlyArray<ExtractedEvent>) => {
    const taste = favorites.tasteIds;
    if (taste.size === 0) return;
    let changed = false;
    setSnapshots((prev) => {
      const next = new Map(prev);
      for (const e of events) {
        if (!taste.has(e.rowKey)) continue;
        const existing = next.get(e.rowKey);
        const snap = toSnapshot(e);
        // Cheap equality: compare serialized form only when a key is new or the
        // tag/category signals might have changed.
        if (!existing || JSON.stringify(existing) !== JSON.stringify(snap)) {
          next.set(e.rowKey, snap);
          changed = true;
        }
      }
      if (!changed) return prev;
      snapshotKeysRef.current = new Set(next.keys());
      writeCachedSnapshots(Array.from(next.values()));
      return next;
    });
  }, [favorites.tasteIds]);

  // Only score against events the user still has marked. Prunes snapshots for
  // events the user un-marked (or that were removed) without needing a write —
  // the persisted set self-heals on the next registerEvents call.
  const activeSnapshots = useMemo(() => {
    const taste = favorites.tasteIds;
    const list: TasteProfileInput[] = [];
    for (const [id, snap] of snapshots) {
      if (taste.has(id)) list.push(snap);
    }
    return list;
  }, [snapshots, favorites.tasteIds]);

  const profile = useMemo(() => buildTasteProfile(activeSnapshots), [activeSnapshots]);

  const weights = tasteSettings.weights;

  const match = useCallback((event: ExtractedEvent, embeddingScore?: number): TasteMatch | null => {
    if (profile.size < MIN_TASTE_FOR_SCORING) return null;
    // Don't badge the marked events themselves — they're known, not matches.
    if (profile.markedRowKeys.has(event.rowKey)) return null;
    const { score, reasons } = scoreEvent(event, profile, embeddingScore, weights);
    if (score < MIN_TASTE_SCORE_THRESHOLD) return null;
    return { score, percent: Math.round(score * 100), level: matchLevel(score), reasons };
  }, [profile, weights]);

  const value = useMemo<TasteMatchContextValue>(() => ({
    ready: profile.size >= MIN_TASTE_FOR_SCORING,
    profileSize: profile.size,
    match,
    registerEvents,
  }), [profile.size, match, registerEvents]);

  return <TasteMatchContext.Provider value={value}>{children}</TasteMatchContext.Provider>;
}

const NOOP_VALUE: TasteMatchContextValue = {
  ready: false,
  profileSize: 0,
  match: () => null,
  registerEvents: () => { /* no-op */ },
};

export function useTasteMatch(): TasteMatchContextValue {
  return useContext(TasteMatchContext) ?? NOOP_VALUE;
}
